/* ============================================================
   ModifierSheet — variant / head / addon picker for the Till.
   Owner ask Feb 2026: big touch buttons for variants, head, addon
   groups, legacy flat add-ons (shisha bases, ice pipe, shots,
   extra coal), spice level + notes for food, and a custom-charge
   form for the manager-only "Custom" tile.

   Extracted from POSPage.jsx (Feb 2026) — second step of the
   POSPage refactor described in PRD.md → P2 backlog. Behaviour
   and every data-testid are unchanged from the in-file version
   so the existing pytest + Playwright suite keeps working.
   ============================================================ */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Minus } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";

// Stable random key for cart lines so two identical items with different
// modifier picks stay separate rows (e.g. two Macallan 12 with different
// ice prefs). Kept in lockstep with the copy in POSPage.jsx.
const cartLineKey = () =>
  `cl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function ModifierSheet({ item, open, onClose, onAdd }) {
  const [variant, setVariant] = useState(null);
  const [head, setHead] = useState(null);
  const [addons, setAddons] = useState({}); // groupId -> [optionName] (multi) or optionName (single)
  const [flatAddons, setFlatAddons] = useState([]); // legacy flat add_ons: array of names
  const [spice, setSpice] = useState("");
  const [instructions, setInstructions] = useState("");
  const [qty, setQty] = useState(1);

  // Custom-product manager-only fields
  const [customName, setCustomName] = useState("");
  const [customPrice, setCustomPrice] = useState("");

  useEffect(() => {
    if (!item) return;
    // Defaults: pick first variant, "Standard" head (or first), no addons.
    setVariant(item.variants && item.variants.length > 0 ? item.variants[0].label : null);
    setHead(
      item.head_options && item.head_options.length > 0
        ? item.head_options[0].label
        : null
    );
    setAddons({});
    setFlatAddons([]);
    setSpice("");
    setInstructions("");
    setQty(1);
    setCustomName("");
    setCustomPrice("");
  }, [item?.id, open]);

  const toggleAddon = (group, optName) => {
    setAddons((prev) => {
      const next = { ...prev };
      if (group.select_mode === "multi") {
        const cur = new Set(next[group.id] || []);
        if (cur.has(optName)) cur.delete(optName);
        else cur.add(optName);
        next[group.id] = Array.from(cur);
      } else {
        next[group.id] = next[group.id] === optName ? null : optName;
      }
      return next;
    });
  };

  const toggleFlatAddon = (name) => {
    setFlatAddons((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const unitPrice = useMemo(() => {
    if (!item) return 0;
    if (item.is_custom_charge) return parseFloat(customPrice) || 0;
    const v =
      item.variants?.find((vv) => vv.label === variant)?.price ?? item.price ?? 0;
    const h = item.head_options?.find((hh) => hh.label === head)?.price ?? 0;
    let addonTotal = 0;
    for (const g of item.addon_groups || []) {
      const picks = addons[g.id];
      const names = Array.isArray(picks) ? picks : picks ? [picks] : [];
      for (const opt of g.options || []) {
        if (names.includes(opt.name)) addonTotal += opt.price || 0;
      }
    }
    // Legacy flat add_ons (shisha bases, ice pipe, shots, extra coal etc.)
    for (const a of item.add_ons || []) {
      if (flatAddons.includes(a.name)) addonTotal += Number(a.price) || 0;
    }
    return v + h + addonTotal;
  }, [item, variant, head, addons, flatAddons, customPrice]);

  if (!item) return null;

  const handleAdd = () => {
    if (item.is_custom_charge) {
      if (!customName.trim()) {
        toast.error("Custom product needs a name");
        return;
      }
      if (!(parseFloat(customPrice) > 0)) {
        toast.error("Custom price must be > $0");
        return;
      }
    }

    // Build addons payload — flatten to the array format /api/orders expects
    const addonSnaps = [];
    for (const g of item.addon_groups || []) {
      const picks = addons[g.id];
      const names = Array.isArray(picks) ? picks : picks ? [picks] : [];
      for (const opt of g.options || []) {
        if (names.includes(opt.name)) {
          addonSnaps.push({
            name: opt.name,
            price: opt.price || 0,
            group_id: g.id,
          });
        }
      }
    }
    // Flat add_ons (shisha bases / ice pipe / shots / extra coal) — same
    // payload shape, no group_id.
    for (const a of item.add_ons || []) {
      if (flatAddons.includes(a.name)) {
        addonSnaps.push({ name: a.name, price: Number(a.price) || 0 });
      }
    }

    // Build display label so cart row is informative
    const labelBits = [];
    if (variant) labelBits.push(variant);
    if (head) labelBits.push(head);
    if (addonSnaps.length) labelBits.push(addonSnaps.map((a) => a.name).join(", "));
    if (spice) labelBits.push(spice);

    const line = {
      _key: cartLineKey(),
      item_id: item.id,
      name: item.is_custom_charge ? `Custom · ${customName.trim()}` : item.name,
      category: item.category,
      quantity: qty,
      unit_price: unitPrice,
      variant_label: variant,
      head_label: head,
      add_ons: addonSnaps,
      spice_level: item.category === "food" ? spice || null : null,
      instructions: item.category === "food" ? instructions.slice(0, 240) : null,
      custom_name: item.is_custom_charge ? customName.trim() : null,
      custom_price: item.is_custom_charge ? parseFloat(customPrice) : null,
      custom_notes: item.is_custom_charge ? instructions.slice(0, 240) : null,
      modifier_label: labelBits.join(" · ") || null,
    };
    onAdd(line);
    onClose();
  };

  const groups = item.addon_groups || [];
  const showSpice = item.category === "food";

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="!w-[460px] !max-w-[80vw] bg-zinc-950 border-l border-white/10 overflow-y-auto"
        data-testid="pos-modifier-sheet"
      >
        <SheetHeader>
          <SheetTitle className="text-white font-serif text-xl pr-8">
            {item.name}
          </SheetTitle>
        </SheetHeader>

        {item.is_custom_charge && (
          <div className="mt-4 space-y-2">
            <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
              Custom product
            </p>
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="Product name"
              className="w-full bg-zinc-900 border border-white/10 rounded px-3 py-2 text-white text-sm"
              data-testid="pos-custom-name"
            />
            <input
              type="number"
              step="0.01"
              min="0"
              value={customPrice}
              onChange={(e) => setCustomPrice(e.target.value)}
              placeholder="Price"
              className="w-full bg-zinc-900 border border-white/10 rounded px-3 py-2 text-white text-sm"
              data-testid="pos-custom-price"
            />
          </div>
        )}

        {item.variants && item.variants.length > 0 && (
          <div className="mt-5">
            <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
              Size
            </p>
            <div className="grid grid-cols-2 gap-2">
              {item.variants.map((v) => (
                <button
                  key={v.label}
                  type="button"
                  onClick={() => setVariant(v.label)}
                  className={`rounded-md border px-3 py-3 text-sm font-sans transition ${
                    variant === v.label
                      ? "border-gold bg-gold/20 text-gold"
                      : "border-white/10 bg-zinc-900 text-zinc-300 hover:border-white/30"
                  }`}
                  data-testid={`pos-variant-${v.label}`}
                >
                  <div>{v.label}</div>
                  <div className="text-[10px] mt-0.5 opacity-70">{money(v.price)}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {item.head_options && item.head_options.length > 0 && (
          <div className="mt-5">
            <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
              Head
            </p>
            <div className="grid grid-cols-2 gap-2">
              {item.head_options.map((h) => (
                <button
                  key={h.label}
                  type="button"
                  onClick={() => setHead(h.label)}
                  className={`rounded-md border px-3 py-3 text-sm font-sans transition ${
                    head === h.label
                      ? "border-gold bg-gold/20 text-gold"
                      : "border-white/10 bg-zinc-900 text-zinc-300 hover:border-white/30"
                  }`}
                  data-testid={`pos-head-${h.label}`}
                >
                  <div>{h.label}</div>
                  <div className="text-[10px] mt-0.5 opacity-70">
                    {h.price ? `+${money(h.price)}` : "incl."}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {groups.map((g) => (
          <div key={g.id} className="mt-5">
            <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
              {g.label}{" "}
              <span className="opacity-60 normal-case tracking-normal">
                ({g.select_mode === "multi" ? "pick any" : "pick one"})
              </span>
            </p>
            <div className="grid grid-cols-2 gap-2">
              {(g.options || []).map((opt) => {
                const picks = addons[g.id];
                const selected = Array.isArray(picks)
                  ? picks.includes(opt.name)
                  : picks === opt.name;
                return (
                  <button
                    key={opt.name}
                    type="button"
                    onClick={() => toggleAddon(g, opt.name)}
                    className={`rounded-md border px-3 py-2 text-xs font-sans transition text-left ${
                      selected
                        ? "border-gold bg-gold/20 text-gold"
                        : "border-white/10 bg-zinc-900 text-zinc-300 hover:border-white/30"
                    }`}
                    data-testid={`pos-addon-${g.id}-${opt.name}`}
                  >
                    <div>{opt.name}</div>
                    {opt.price > 0 && (
                      <div className="text-[10px] mt-0.5 opacity-70">+{money(opt.price)}</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {/* Legacy flat add_ons — same shape the customer QR menu uses for
            shisha (Ice Pipe / Milk Base / Red Bull Base / Vodka·Whiskey·
            Tequila Shot / Extra Coal). Treated as a multi-select since the
            customer page mirrors that behaviour. */}
        {item.add_ons && item.add_ons.length > 0 && (
          <div className="mt-5">
            <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
              Add-ons{" "}
              <span className="opacity-60 normal-case tracking-normal">(pick any)</span>
            </p>
            <div className="grid grid-cols-2 gap-2">
              {item.add_ons.map((a) => {
                const selected = flatAddons.includes(a.name);
                return (
                  <button
                    key={a.name}
                    type="button"
                    onClick={() => toggleFlatAddon(a.name)}
                    className={`rounded-md border px-3 py-2 text-xs font-sans transition text-left ${
                      selected
                        ? "border-gold bg-gold/20 text-gold"
                        : "border-white/10 bg-zinc-900 text-zinc-300 hover:border-white/30"
                    }`}
                    data-testid={`pos-flat-addon-${a.name}`}
                  >
                    <div>{a.name}</div>
                    <div className="text-[10px] mt-0.5 opacity-70">
                      {Number(a.price) > 0 ? `+${money(a.price)}` : "Free"}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {showSpice && (
          <div className="mt-5">
            <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
              Spice level
            </p>
            <div className="grid grid-cols-4 gap-2">
              {["Mild", "Medium", "Hot", "Extra Hot"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSpice(spice === s ? "" : s)}
                  className={`rounded-md border px-2 py-2 text-xs font-sans transition ${
                    spice === s
                      ? "border-gold bg-gold/20 text-gold"
                      : "border-white/10 bg-zinc-900 text-zinc-300 hover:border-white/30"
                  }`}
                  data-testid={`pos-spice-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {(showSpice || item.is_custom_charge) && (
          <div className="mt-5">
            <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
              Notes (optional)
            </p>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value.slice(0, 240))}
              placeholder="No onions, allergy etc."
              rows={2}
              className="w-full bg-zinc-900 border border-white/10 rounded px-3 py-2 text-white text-sm"
              data-testid="pos-instructions"
            />
          </div>
        )}

        <div className="mt-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 bg-zinc-900 border border-white/10 rounded-md p-1">
            <button
              type="button"
              onClick={() => setQty(Math.max(1, qty - 1))}
              className="w-10 h-10 grid place-items-center text-white hover:bg-white/5 rounded"
              data-testid="pos-qty-minus"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="w-8 text-center font-mono text-white" data-testid="pos-qty-value">
              {qty}
            </span>
            <button
              type="button"
              onClick={() => setQty(qty + 1)}
              className="w-10 h-10 grid place-items-center text-white hover:bg-white/5 rounded"
              data-testid="pos-qty-plus"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <Button
            onClick={handleAdd}
            className="flex-1 h-12 bg-gold hover:bg-gold/90 text-black font-sans font-semibold uppercase tracking-[0.12em]"
            data-testid="pos-add-line"
          >
            Add · {money(unitPrice * qty)}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
